import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Page } from '@hud/web-components/Layout/Page';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import useEnvironment from '../../../hooks/useEnvironment';
import { useGlobalQueryString } from '../../../hooks/useGlobalFilter';
import useIssue from '../../../hooks/useIssue';
import {
  entityExploreSubpage,
  entityIcon,
  entityText,
  HeadsUpEntity,
  HeadsUpService,
  HeadsUpValue,
  serviceAndFlowParams,
} from '../HeadsUpCommon';
import { Container } from '@hud/web-components/Container/Container';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { formatDateTime } from '@hud/web-components/utils';
import { PublicIssue } from '@hud/portal-dtos/issues-v2.dtos';
import { isEndpointEntity, isKafkaTopicEntity } from '@hud/portal-dtos/issues.dtos';
import IssuesMenuWebappContainer from '../../../components/IssuesMenuWebappContainer/IssuesMenuWebappContainer';
import { LinkCell } from '@hud/web-components/Table/Table';
import useRule from '../../../hooks/useRule';
import { Rule } from '@hud/rules-dtos/api-rules.dtos';
import Tooltip from '@hud/web-components/Tooltip/Tooltip';
import { Markdown } from '@hud/web-components/Markdown/Markdown';
import Exemplar from '@hud/web-components/src/Exemplar/Exemplar';
import FixItPrompt from '@hud/web-components/FixItPrompt/FixItPrompt';
import { useBooleanFeatureFlag } from '../../../hooks/useFeatureFlags';
import useAnalytics from '../../../hooks/useAnalytics';
import { stringifyTimeFrame } from '../../../utils';
import { MethodsPopover } from '../../../components/MethodsPopover/MethodsPopover';
import * as Styled from './HeadsUpIssuePage.styles';
import { CellIcon } from '../styles';

export default function HeadsUpIssuePage() {
  const { issueId } = useParams<{ issueId: string }>();
  const { environment } = useEnvironment();
  const globalUrlParams = useGlobalQueryString();
  const exemplarsEnabled = useBooleanFeatureFlag('show-exemplars');
  const analytics = useAnalytics();

  const issueQuery = useIssue(parseInt(issueId!), {
    environmentName: environment?.name || '',
  });

  const issue = issueQuery.data;

  const ruleQuery = useRule(issue?.ruleId);

  const mainEntity = issue?.mainEntity;
  const mainEntityTitle = entityText(mainEntity);

  useEffect(() => {
    if (issueQuery.isSuccess) {
      document.title = `${mainEntityTitle} • Hud`;
    } else {
      document.title = 'Hud';
    }
  }, [mainEntityTitle, issueQuery.isSuccess]);

  const sendCopyPromptAnalytics = () => {
    analytics.capture('fix_it_prompt_copied_to_clipboard', { issue_id: issueId || '' });
  };

  if (issueQuery.isError) {
    console.error(issueQuery.error);
    return (
      <Page
        header={'Heads Up'}
        actions={
          <ActionsContainer>
            <EnvironmentMenu enabled={false} />
            {/* No time frame picker in this page */}
            <ThemeButton />
          </ActionsContainer>
        }
      >
        {issueQuery.error.response?.status === 404
          ? issueQuery.error.response?.data.message
          : 'Something went wrong'}
      </Page>
    );
  }

  const flowId =
    mainEntity && (isEndpointEntity(mainEntity) || isKafkaTopicEntity(mainEntity))
      ? mainEntity.id
      : undefined;

  const loading = issueQuery.isLoading || !issue;
  const newURLParams = new URLSearchParams(globalUrlParams);
  if (issue?.timeFrame) {
    const timeframeUrlParam = stringifyTimeFrame(issue.timeFrame, { normalize: true });
    newURLParams.set('timeFrame', timeframeUrlParam);
  }

  const defaultBreadcrumbs = [{ title: 'Issues', to: '/heads-up/issues', nowrap: true }];
  const issueBreadCrumbs = [];
  if (mainEntity && isEndpointEntity(mainEntity)) {
    issueBreadCrumbs.push({ title: <MethodsPopover methods={mainEntity.methods} /> });
    issueBreadCrumbs.push({ title: mainEntity.path });
  } else {
    issueBreadCrumbs.push({ title: entityText(mainEntity) });
  }

  return (
    <Page
      header={loading ? 'loading' : [...defaultBreadcrumbs, ...issueBreadCrumbs]}
      actions={
        <ActionsContainer>
          <EnvironmentMenu enabled={false} />
          {/* No time frame picker in this page */}
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <Container
        title={
          loading ? (
            <Skeleton width={200} />
          ) : (
            <ContainerTitle issue={issue} rule={ruleQuery.data?.rule} />
          )
        }
        variant="solid"
        topRightSection={issue ? <TopRightSection issue={issue} /> : null}
        aria-label="Issue Overview"
      >
        {/* Main entity */}
        <Styled.CustomRow title="Main entity:" loading={loading}>
          <HeadsUpEntity
            entity={issue?.mainEntity}
            serviceId={issue?.serviceId}
            flowId={flowId}
            globalUrlParams={newURLParams.toString()}
          />
        </Styled.CustomRow>

        {/* Service */}
        <Styled.CustomRow title="Service:" loading={loading}>
          <HeadsUpService
            serviceId={issue?.serviceId}
            serviceName={issue?.serviceName}
            globalUrlParams={newURLParams.toString()}
          />
        </Styled.CustomRow>

        {/* Values */}
        {issue?.values.map((value) => (
          <Styled.CustomRow title={value.label} loading={loading}>
            <HeadsUpValue value={value} />
          </Styled.CustomRow>
        ))}

        {/* Sources */}
        <Styled.CustomRow title="Sources:" loading={loading}>
          <Styled.SourcesContainer>
            {issue?.sources.map((source) => (
              <Styled.Source key={source.id}>
                <CellIcon icon={entityIcon(source)} size={'20px'} />
                <LinkCell
                  to={`/explore/${entityExploreSubpage(source)}/${
                    isEndpointEntity(source)
                      ? `${source.id}-${issue?.serviceId}`
                      : source.id
                  }?${newURLParams}${serviceAndFlowParams(source, issue?.serviceId, flowId)}`}
                >
                  {entityText(source)}
                </LinkCell>
                {source.values &&
                  source.values.map((value) => (
                    <>
                      <CellIcon icon={'other.dot'} size={'20px'} />
                      <HeadsUpValue value={value} />
                    </>
                  ))}
              </Styled.Source>
            ))}
          </Styled.SourcesContainer>
        </Styled.CustomRow>
      </Container>
      {exemplarsEnabled &&
        issue?.fixItPrompt &&
        (loading ? (
          <Skeleton width={200} />
        ) : (
          <FixItPrompt
            fixItPrompt={issue.fixItPrompt}
            onCopyPrompt={sendCopyPromptAnalytics}
          />
        ))}
      {exemplarsEnabled &&
        (loading ? (
          <Skeleton width={200} />
        ) : (
          issue?.exemplars.map((exemplar) => <Exemplar exemplar={exemplar} />)
        ))}
    </Page>
  );
}

function TopRightSection({ issue }: { issue: PublicIssue }) {
  return (
    <Styled.RightSection>
      {formatDateTime(issue.detectionTime)}{' '}
      {isEndpointEntity(issue.mainEntity) && <IssuesMenuWebappContainer issue={issue} />}
    </Styled.RightSection>
  );
}

export const ContainerTitle = ({
  issue,
  rule,
}: {
  issue: PublicIssue;
  rule?: Omit<Rule, 'enabledServices' | 'totalServices'>;
}) => {
  return (
    <Styled.TitleContainer>
      <Styled.TitleIcon icon="other.flame" size={'20px'} /> {issue.title}
      {rule?.shortDescription && (
        <Tooltip description={<Markdown>{rule.shortDescription}</Markdown>} />
      )}
    </Styled.TitleContainer>
  );
};
